// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';

import { momentTz } from '../utils/dates';
import {
  DATE_FORMAT_HH_MM,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  NOOP,
} from '../const/CommonConst';
import { isRichContentEmpty, isValueEmpty } from '../utils/Utils';
import { createInitialFormValues } from '../utils/formUtils';
import {
  formatDraftFieldTimestamp,
  resolveDraftEditorDisplayName,
} from '../components/Common/AttributesForm/FieldComponents/DraftFieldExtras';

const NON_DRAFT_INPUT_NAMES = ['template_version_number'];
const DRAFT_SAVE_IDLE_MS = 2000;
const DRAFT_DEBOUNCE_MS = 1000;

// Attribute types that render inside a modal — draft fires only on modal Save (persist:true)
const MODAL_DRAFT_DATA_TYPES = [
  'Table',
  'AdvancedTable',
  'Chart',
  'Checklist',
  'Document',
  'Paragraph',
];
const MODAL_DRAFT_INPUT_TYPES = ['TEXTFIELD_EXTENDED'];

const DraftUiContext = createContext({
  draftAttributeIds: EMPTY_ARRAY,
  draftEditorsByAttributeId: EMPTY_OBJECT,
  draftUpdatedAtByAttributeId: EMPTY_OBJECT,
  draftData: EMPTY_OBJECT,
});

const useDraftUi = () => useContext(DraftUiContext);

export const getValueByAttributeId = (values, attributeId) => {
  if (values == null || attributeId == null) return undefined;
  const keys = [attributeId, String(attributeId)];
  const numericId = Number(attributeId);
  if (!Number.isNaN(numericId)) keys.push(numericId);
  const match = keys.find((key) => Object.hasOwn(values, key));
  return match === undefined ? undefined : values[match];
};

export const getFormikValueByAttributeId = (formik, attributeId) =>
  formik?.values && attributeId != null
    ? getValueByAttributeId(formik.values, attributeId)
    : undefined;

const originalValue = (values, id) => getValueByAttributeId(values, id) ?? null;

const unwrapDraftValue = (value) =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.hasOwn(value, 'new_value')
    ? value.new_value
    : value;

const findAttribute = (attributes, id) =>
  attributes?.find((attribute) => String(attribute?.id) === String(id));

const mapDraftIdsToAttributeKeys = (draftMap, attributes) =>
  Object.fromEntries(
    Object.entries(draftMap || {}).map(([id, value]) => {
      const attribute = findAttribute(attributes, id);
      return [attribute?.id ?? id, value];
    })
  );

export const buildDraftChipTooltip = (editorName, updatedAt) => {
  const lines = [];
  if (editorName) lines.push(`Last updated by ${editorName}`);
  const formatted = formatDraftFieldTimestamp(updatedAt);
  if (formatted) lines.push(formatted);
  return lines.length ? lines.join('\n') : 'Draft changes pending';
};

export const attributeShowsDraftInput = (attribute) => {
  const { data_type: dataType, name } = attribute ?? {};
  if (NON_DRAFT_INPUT_NAMES.includes(name)) return false;
  const canEdit =
    attribute?.is_editable ||
    (attribute?.is_derived && attribute?.permission === 'manage');
  return canEdit && Boolean(dataType);
};

export const countDraftAttributesInList = (attributes, draftAttributeIds) => {
  if (!attributes?.length || !draftAttributeIds?.length) return 0;
  const draftIds = new Set(draftAttributeIds.map(String));
  return attributes.reduce(
    (count, attribute) =>
      attribute?.id != null &&
      draftIds.has(String(attribute.id)) &&
      attributeShowsDraftInput(attribute)
        ? count + 1
        : count,
    0
  );
};

const getUserLookupValue = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value.user_id ?? value.username ?? value.user ?? null;
  }
  return value;
};

const normalizeUsersList = (usersList) =>
  (usersList || []).map((user) => ({
    ...user,
    username: user?.username ?? user?.user,
  }));

const findUser = (value, usersList) => {
  const lookup = getUserLookupValue(value);
  if (lookup == null || lookup === '' || !usersList?.length) return null;
  return usersList.find((user) => {
    const username = user?.username ?? user?.user;
    return (
      username === lookup ||
      user?.user_id === lookup ||
      String(user?.user_id) === String(lookup)
    );
  });
};

const getUserIdentityKey = (value, usersList) => {
  const user = findUser(value, usersList);
  if (user?.user_id != null) return String(user.user_id);
  const lookup = getUserLookupValue(value);
  return lookup == null || lookup === '' ? null : String(lookup);
};

const isUserMultiSelect = (attribute) =>
  attribute?.data_type === 'User' &&
  attribute?.input_control_type === 'MULTI_SELECT';

const normalizeUserValueForForm = (value, usersList, attribute) => {
  if (value == null || value === '') return value;

  if (isUserMultiSelect(attribute) || Array.isArray(value)) {
    const items = Array.isArray(value) ? value : [value];
    const ids = items
      .map(
        (item) => findUser(item, usersList)?.user_id ?? getUserLookupValue(item)
      )
      .filter((id) => id != null && id !== '');
    return ids.length ? ids : null;
  }

  const user = findUser(value, usersList);
  if (user) return user.username ?? user.user ?? getUserLookupValue(value);
  return getUserLookupValue(value);
};

export const draftValuesEqual = (
  draftVal,
  originalVal,
  attribute,
  usersList
) => {
  const empty = (v) => v == null || v === '' || (Array.isArray(v) && !v.length);
  if (empty(draftVal) && empty(originalVal)) return true;
  const norm = (v) => (v === undefined || v === null || v === '' ? null : v);
  if (isEqual(norm(draftVal), norm(originalVal))) return true;
  if (attribute?.data_type === 'Association') {
    const extractId = (v) =>
      v != null && typeof v === 'object' ? v?.entity_id : v;
    const draftId = extractId(draftVal);
    const origId = extractId(originalVal);
    if (draftId == null && origId == null) return true;
    if (draftId != null && origId != null)
      return String(draftId) === String(origId);
    return false;
  }
  if (attribute?.data_type !== 'User') return isEqual(draftVal, originalVal);

  const normalizedUsers = normalizeUsersList(usersList);
  const toIdentityKeys = (v) => {
    if (v == null || v === '') return [];
    return (Array.isArray(v) ? v : [v])
      .map((item) => getUserIdentityKey(item, normalizedUsers))
      .filter(Boolean)
      .sort();
  };

  if (
    isUserMultiSelect(attribute) ||
    Array.isArray(draftVal) ||
    Array.isArray(originalVal)
  ) {
    return isEqual(toIdentityKeys(draftVal), toIdentityKeys(originalVal));
  }

  const draftKey = getUserIdentityKey(draftVal, normalizedUsers);
  const originalKey = getUserIdentityKey(originalVal, normalizedUsers);
  if (draftKey == null && originalKey == null) return true;
  if (draftKey != null && originalKey != null) return draftKey === originalKey;

  const draftLookup = getUserLookupValue(draftVal);
  const originalLookup = getUserLookupValue(originalVal);
  return (
    draftLookup != null &&
    originalLookup != null &&
    String(draftLookup) === String(originalLookup)
  );
};

const isAttributeValueFilled = (value, attribute) => {
  if (value == null) return false;
  const type = attribute?.data_type;
  if (type === 'Paragraph') return !isRichContentEmpty(value);
  if (type === 'Checklist') return !isValueEmpty(value) && Boolean(value?.type);
  if (type === 'Document') {
    return Array.isArray(value) && Boolean(value[0]?.document_id);
  }
  return !isValueEmpty(value);
};

export const computeOverallAttributeProgress = (
  attributes = EMPTY_ARRAY,
  savedValues = EMPTY_OBJECT,
  draftValues = EMPTY_OBJECT
) => {
  const total = attributes.length;
  if (!total) return { complete: 0, total: 0, percent: 0 };
  const complete = attributes.reduce((count, attribute) => {
    if (attribute?.id == null) return count;
    const filled = (values) =>
      isAttributeValueFilled(originalValue(values, attribute.id), attribute);
    return filled(savedValues) || filled(draftValues) ? count + 1 : count;
  }, 0);
  return { complete, total, percent: (complete / total) * 100 };
};

const getTrueDraftDiffs = (
  entries,
  originalValues,
  allAttributes,
  usersList
) => {
  const trueDraftData = {};
  const editorsByAttributeId = {};
  const updatedAtByAttributeId = {};
  entries.forEach(({ id, value, updatedBy, updatedAt }) => {
    const attrId = String(id);
    const attribute = findAttribute(allAttributes, attrId);
    if (
      draftValuesEqual(
        value,
        originalValue(originalValues, attrId),
        attribute,
        usersList
      )
    ) {
      return;
    }
    trueDraftData[attrId] = value;
    if (updatedBy) editorsByAttributeId[attrId] = updatedBy;
    if (updatedAt) updatedAtByAttributeId[attrId] = updatedAt;
  });
  return { trueDraftData, editorsByAttributeId, updatedAtByAttributeId };
};

const filterDraftValuesForSave = (mergedDraft, attributes) =>
  Object.fromEntries(
    Object.entries(mergedDraft || {}).filter(([draftId, value]) => {
      const attribute = findAttribute(attributes, draftId);
      if (attribute?.data_type !== 'Document') return true;
      const cleared = isValueEmpty(value);
      const hasId =
        Array.isArray(value) && value.length > 0 && value[0].document_id;
      return cleared || hasId;
    })
  );

const resolveTrueDraftData = (mergedDraft, originals, attributes, users) =>
  getTrueDraftDiffs(
    Object.entries(filterDraftValuesForSave(mergedDraft, attributes)).map(
      ([id, value]) => ({ id, value })
    ),
    originals,
    attributes,
    users
  ).trueDraftData;

const parseDraftEntriesFromResponse = (response) => {
  const data = response?.data;
  if (data == null) return [];
  const list = Array.isArray(data) ? data : data?.draft_attributes;
  if (Array.isArray(list)) {
    return list
      .map((item) => ({
        id: item.attribute_id ?? item.id,
        value: unwrapDraftValue(
          item.draft_value ?? item.new_value ?? item.value
        ),
        updatedBy: item.updated_by,
        updatedAt: item.updated_at,
      }))
      .filter((entry) => entry.id != null);
  }
  const draftMap = data?.draft ?? data?.data?.draft;
  if (!draftMap || typeof draftMap !== 'object' || Array.isArray(draftMap))
    return [];
  const responseUpdatedBy = data?.updated_by ?? null;
  return Object.entries(draftMap).map(([id, entry]) => ({
    id,
    value: unwrapDraftValue(entry),
    updatedBy: responseUpdatedBy,
  }));
};

const getDraftMetaFromResponse = (response) => {
  const data = response?.data ?? response ?? EMPTY_OBJECT;
  return {
    updatedBy: data?.updated_by ?? null,
    updatedAt: data?.updated_at ?? null,
  };
};

const buildEditorsByAttributeId = (
  existing,
  attributeIds,
  topUpdatedBy,
  usersList,
  perAttributeEditors = null
) => {
  const next = { ...(existing || {}) };
  Object.entries(perAttributeEditors || {}).forEach(([id, updatedBy]) => {
    const name = resolveDraftEditorDisplayName(updatedBy, usersList);
    if (name) next[id] = name;
  });
  const topName = resolveDraftEditorDisplayName(topUpdatedBy, usersList);
  if (topName && attributeIds?.length) {
    attributeIds.forEach((id) => {
      next[String(id)] = topName;
    });
  }
  return next;
};

const fillMissingTimestamps = (existing, attributeIds, fallbackTime) => {
  if (!fallbackTime || !attributeIds?.length) return existing || {};
  const next = { ...(existing || {}) };
  attributeIds.forEach((id) => {
    const key = String(id);
    if (!next[key]) next[key] = fallbackTime;
  });
  return next;
};

const pickLatestDraftTimestamp = (updatedAtByAttributeId) => {
  const values = Object.values(updatedAtByAttributeId || {}).filter(Boolean);
  if (!values.length) return null;
  return values.reduce((latest, current) =>
    momentTz(current).isAfter(momentTz(latest)) ? current : latest
  );
};

const buildDraftStateFromResponse = (
  response,
  originalValues,
  allAttributes,
  usersList
) => {
  const entries = parseDraftEntriesFromResponse(response);
  const {
    trueDraftData,
    editorsByAttributeId: editorsFromEntries,
    updatedAtByAttributeId: timesFromEntries,
  } = getTrueDraftDiffs(entries, originalValues, allAttributes, usersList);
  const { updatedBy, updatedAt } = getDraftMetaFromResponse(response);
  const draftIds = Object.keys(trueDraftData);
  return {
    trueDraftData,
    editorsByAttributeId: buildEditorsByAttributeId(
      {},
      draftIds,
      updatedBy,
      usersList,
      editorsFromEntries
    ),
    updatedAtByAttributeId: fillMissingTimestamps(
      timesFromEntries,
      draftIds,
      updatedAt ?? entries[0]?.updatedAt ?? null
    ),
  };
};

export const buildDraftApiPayload = (attributeId, value) => ({
  [String(attributeId)]: { new_value: value },
});

export const composeDraftBlurHandler = (formikBlur, onDraftBlur) => (event) => {
  if (typeof formikBlur === 'function') formikBlur(event);
  onDraftBlur?.();
};

/** After Autocomplete clear (X), blur can run before Formik updates — persist on next tick. */
export const scheduleDraftPersistOnBlur = (onDraftBlur) => {
  if (typeof onDraftBlur === 'function') {
    queueMicrotask(() => onDraftBlur());
  }
};

export const DraftAttributeIdsProvider = ({
  draftAttributeIds = EMPTY_ARRAY,
  draftEditorsByAttributeId = EMPTY_OBJECT,
  draftUpdatedAtByAttributeId = EMPTY_OBJECT,
  draftData = EMPTY_OBJECT,
  children,
}) => {
  const value = useMemo(
    () => ({
      draftAttributeIds,
      draftEditorsByAttributeId,
      draftUpdatedAtByAttributeId,
      draftData,
    }),
    [
      draftAttributeIds,
      draftEditorsByAttributeId,
      draftUpdatedAtByAttributeId,
      draftData,
    ]
  );
  return (
    <DraftUiContext.Provider value={value}>{children}</DraftUiContext.Provider>
  );
};

DraftAttributeIdsProvider.propTypes = {
  draftAttributeIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  draftEditorsByAttributeId: PropTypes.objectOf(PropTypes.string),
  draftUpdatedAtByAttributeId: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  draftData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  children: PropTypes.node,
};

export const useDraftAttributeValue = (attributeId) => {
  const { draftData } = useDraftUi();
  return useMemo(
    () => getValueByAttributeId(draftData, attributeId),
    [attributeId, draftData]
  );
};

export const useDraftAttributeIds = () => useDraftUi().draftAttributeIds;

export const useDraftChipMeta = (attributeId) => {
  const { draftEditorsByAttributeId, draftUpdatedAtByAttributeId } =
    useDraftUi();
  return useMemo(() => {
    if (attributeId == null) return { editorName: null, updatedAt: null };
    const key = String(attributeId);
    return {
      editorName: draftEditorsByAttributeId[key] ?? null,
      updatedAt: draftUpdatedAtByAttributeId[key] ?? null,
    };
  }, [attributeId, draftEditorsByAttributeId, draftUpdatedAtByAttributeId]);
};

export const useShowDraftChip = (attribute) => {
  const { draftAttributeIds } = useDraftUi();
  return useMemo(() => {
    if (attribute?.id == null || !draftAttributeIds?.length) return false;
    const id = String(attribute.id);
    return (
      draftAttributeIds.some((draftId) => String(draftId) === id) &&
      attributeShowsDraftInput(attribute)
    );
  }, [attribute, draftAttributeIds]);
};

export const useDraftValuesCount = (attributes) => {
  const { draftAttributeIds } = useDraftUi();
  return useMemo(
    () => countDraftAttributesInList(attributes, draftAttributeIds),
    [attributes, draftAttributeIds]
  );
};

const useDraftAutoSave = ({
  entityId,
  getDraftAttributeValue,
  setDraftAttributeValue,
  isMultipleEditMode,
  formikForm,
  allAttributes = [],
  attributeValues = EMPTY_OBJECT,
  userList = [],
  handleGetAttributesData = NOOP,
  updateInitialValuesFromAPI: _updateInitialValuesFromAPI = NOOP,
  draftResponse = undefined,
}) => {
  const [draftData, setDraftData] = useState({});
  const [draftEditorsByAttributeId, setDraftEditorsByAttributeId] = useState(
    {}
  );
  const [draftUpdatedAtByAttributeId, setDraftUpdatedAtByAttributeId] =
    useState(EMPTY_OBJECT);
  const [draftTimestamp, setDraftTimestamp] = useState(null);
  const [hasDraftChanges, setHasDraftChanges] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const lastSavedDraftRef = useRef({});
  const draftDataRef = useRef(draftData);
  const attributeValuesRef = useRef(attributeValues);
  const formikFormRef = useRef(formikForm);
  const allAttributesRef = useRef(allAttributes);
  const userListRef = useRef(userList);
  const isFetchingDraftRef = useRef(false);
  const isDraftHydrationCompleteRef = useRef(false);
  const pendingDraftApplyRef = useRef(null);
  const pendingDraftByAttributeRef = useRef({});
  const draftDisplayPendingRef = useRef({});
  const draftSaveBlockedRef = useRef(false);
  const draftDebounceTimersRef = useRef({});
  // Tracks the most recently started draft-save request so clearDraftValues
  // can wait for it to land before sending the clear, guaranteeing the
  // clear is the last write and can't be overwritten by a stale save.
  const inFlightSaveRequestRef = useRef(Promise.resolve());
  const usesExternalDraftResponse = draftResponse !== undefined;

  const draftAttributeIds = useMemo(() => Object.keys(draftData), [draftData]);

  const getCtx = () => ({
    originals: attributeValuesRef.current,
    attributes: allAttributesRef.current,
    users: normalizeUsersList(userListRef.current),
  });

  const endDraftSave = () =>
    setTimeout(() => setIsSavingDraft(false), DRAFT_SAVE_IDLE_MS);

  const clearPending = (attrId) => {
    delete pendingDraftByAttributeRef.current[attrId];
    delete draftDisplayPendingRef.current[attrId];
    if (draftDebounceTimersRef.current[attrId]) {
      clearTimeout(draftDebounceTimersRef.current[attrId]);
      delete draftDebounceTimersRef.current[attrId];
    }
  };

  const removeSavedDraft = (attrId) => {
    const merged = { ...lastSavedDraftRef.current };
    delete merged[attrId];
    lastSavedDraftRef.current = merged;
    return merged;
  };

  useEffect(() => {
    isDraftHydrationCompleteRef.current = false;
    pendingDraftByAttributeRef.current = {};
    draftDisplayPendingRef.current = {};
    Object.values(draftDebounceTimersRef.current).forEach(clearTimeout);
    draftDebounceTimersRef.current = {};

    // Cancel any pending debounce saves when the entity changes or this
    // hook unmounts, so a stale timer can't fire after the form is gone.
    return () => {
      Object.values(draftDebounceTimersRef.current).forEach(clearTimeout);
      draftDebounceTimersRef.current = {};
    };
  }, [entityId]);

  // Kept in sync during render (not inside useEffect) so these refs never lag
  // a render behind the live props — buildDraftAttributesForPublish reads them
  // synchronously from event handlers and must never see a stale/empty value.
  attributeValuesRef.current = attributeValues;
  draftDataRef.current = draftData;
  formikFormRef.current = formikForm;
  allAttributesRef.current = allAttributes;
  userListRef.current = userList;

  const applyDraftOverrides = (originalValues, trueDraftData) => {
    const formik = formikFormRef.current;
    const attrs = allAttributesRef.current;
    if (!formik || !attrs?.length || !Object.keys(trueDraftData || {}).length)
      return false;

    const mappedDraft = mapDraftIdsToAttributeKeys(trueDraftData, attrs);
    const { users } = getCtx();
    if (Object.keys(formik.values || {}).length > 0) {
      Object.entries(mappedDraft).forEach(([key, value]) => {
        const attribute = findAttribute(attrs, key);
        const nextValue =
          attribute?.data_type === 'User'
            ? normalizeUserValueForForm(value, users, attribute)
            : value;
        formik.setFieldValue(key, nextValue, false);
      });
      return true;
    }

    const normalizedDraft = Object.fromEntries(
      Object.entries(mappedDraft).map(([key, value]) => {
        const attribute = findAttribute(attrs, key);
        return [
          key,
          attribute?.data_type === 'User'
            ? normalizeUserValueForForm(value, users, attribute)
            : value,
        ];
      })
    );

    formik.setValues(
      {
        ...createInitialFormValues(
          attrs,
          originalValues ?? attributeValuesRef.current ?? EMPTY_OBJECT
        ),
        ...normalizedDraft,
      },
      false
    );
    return true;
  };

  const applyDraftValuesToFormik = useCallback(
    (trueDraftData = lastSavedDraftRef.current) =>
      applyDraftOverrides(attributeValuesRef.current, trueDraftData),
    []
  );

  useEffect(() => {
    const pending = pendingDraftApplyRef.current;
    if (!allAttributes?.length || !pending || !formikFormRef.current) return;
    if (applyDraftOverrides(pending.originalValues, pending.trueDraftData)) {
      pendingDraftApplyRef.current = null;
    }
  }, [allAttributes?.length, formikForm]);

  const applyDraftState = useCallback(
    (trueDraftData, editorsByAttributeId, updatedAtByAttributeId) => {
      setDraftData(trueDraftData);
      setDraftEditorsByAttributeId(editorsByAttributeId);
      setDraftUpdatedAtByAttributeId(updatedAtByAttributeId ?? EMPTY_OBJECT);
      const latestTimestamp = pickLatestDraftTimestamp(updatedAtByAttributeId);
      setDraftTimestamp(
        latestTimestamp
          ? momentTz(latestTimestamp).format(DATE_FORMAT_HH_MM)
          : null
      );
      setHasDraftChanges(Object.keys(trueDraftData).length > 0);
      lastSavedDraftRef.current = trueDraftData;
      draftDisplayPendingRef.current = {};
    },
    []
  );

  const applyHydratedDraft = useCallback(
    (response, originalValues) => {
      const { attributes, users } = getCtx();
      const state = buildDraftStateFromResponse(
        response,
        originalValues,
        attributes,
        users
      );
      applyDraftState(
        state.trueDraftData,
        state.editorsByAttributeId,
        state.updatedAtByAttributeId
      );
      if (!Object.keys(state.trueDraftData).length) {
        pendingDraftApplyRef.current = null;
        return;
      }
      pendingDraftApplyRef.current = applyDraftOverrides(
        originalValues,
        state.trueDraftData
      )
        ? null
        : { originalValues, trueDraftData: state.trueDraftData };
    },
    [applyDraftState]
  );

  useEffect(() => {
    if (
      !usesExternalDraftResponse ||
      !entityId ||
      isValueEmpty(attributeValues) ||
      !allAttributes?.length ||
      draftResponse == null
    ) {
      return;
    }
    applyHydratedDraft({ data: draftResponse }, attributeValuesRef.current);
    isDraftHydrationCompleteRef.current = true;
  }, [
    usesExternalDraftResponse,
    draftResponse,
    entityId,
    attributeValues,
    allAttributes?.length,
    applyHydratedDraft,
  ]);

  const fetchDraftValues = useCallback(
    (originalValues = attributeValuesRef.current) => {
      if (
        !entityId ||
        !getDraftAttributeValue ||
        isFetchingDraftRef.current ||
        usesExternalDraftResponse
      ) {
        return Promise.resolve();
      }
      isFetchingDraftRef.current = true;
      setIsLoadingDraft(true);
      return getDraftAttributeValue({ entity_id: entityId })
        .then((response) => applyHydratedDraft(response, originalValues))
        .catch(() => {
          pendingDraftApplyRef.current = null;
          applyDraftState({}, {}, EMPTY_OBJECT);
          lastSavedDraftRef.current = {};
        })
        .finally(() => {
          isFetchingDraftRef.current = false;
          setIsLoadingDraft(false);
          isDraftHydrationCompleteRef.current = true;
        });
    },
    [
      entityId,
      getDraftAttributeValue,
      applyHydratedDraft,
      applyDraftState,
      usesExternalDraftResponse,
    ]
  );
  useEffect(() => {
    const draftDataForApply = draftDataRef.current;
    if (
      !Object.keys(draftDataForApply).length ||
      !formikFormRef.current ||
      !allAttributes?.length
    ) {
      return;
    }
    const frameId = requestAnimationFrame(() => {
      if (draftDataRef.current !== draftDataForApply) return;
      applyDraftOverrides(attributeValuesRef.current, draftDataForApply);
    });
    return () => cancelAnimationFrame(frameId);
  }, [attributeValues, allAttributes?.length, formikForm?.initialValues]);

  const syncDraftDisplay = useCallback((mergedDraft = {}) => {
    const { originals, attributes, users } = getCtx();
    setDraftData(() => {
      const trueDraftData = resolveTrueDraftData(
        {
          ...lastSavedDraftRef.current,
          ...draftDisplayPendingRef.current,
          ...mergedDraft,
        },
        originals,
        attributes,
        users
      );
      setHasDraftChanges(Object.keys(trueDraftData).length > 0);
      return trueDraftData;
    });
  }, []);

  const applyEditorMetaFromResponse = useCallback(
    (response, attributeIds, { forceTimestamps = false } = {}) => {
      const { updatedBy, updatedAt } = getDraftMetaFromResponse(response);
      const users = userListRef.current;
      const ids = attributeIds.map(String);
      const savedAt = updatedAt ?? momentTz().toISOString();
      if (updatedBy) {
        setDraftEditorsByAttributeId((prev) =>
          buildEditorsByAttributeId(prev, ids, updatedBy, users)
        );
      }
      setDraftUpdatedAtByAttributeId((prev) =>
        forceTimestamps
          ? { ...prev, ...Object.fromEntries(ids.map((id) => [id, savedAt])) }
          : fillMissingTimestamps(prev, ids, savedAt)
      );
      setDraftTimestamp(momentTz(savedAt).format(DATE_FORMAT_HH_MM));
    },
    []
  );

  const postDraft = useCallback(
    (draft, onSuccess, onError = () => {}) => {
      if (draftSaveBlockedRef.current) return Promise.resolve();
      if (!entityId || !setDraftAttributeValue) return Promise.resolve();
      setIsSavingDraft(true);
      const requestPromise = setDraftAttributeValue({
        entity_id: entityId,
        draft,
      })
        .then(onSuccess)
        .catch(onError)
        .finally(endDraftSave);
      inFlightSaveRequestRef.current = requestPromise;
      return requestPromise;
    },
    [entityId, setDraftAttributeValue]
  );

  const clearDraftAttribute = useCallback(
    (attributeId, fallbackValue) => {
      if (attributeId == null) return Promise.resolve();
      const attrId = String(attributeId);
      const targetVal =
        fallbackValue !== undefined
          ? fallbackValue
          : originalValue(attributeValuesRef.current, attrId);
      return postDraft(buildDraftApiPayload(attrId, targetVal), () => {
        removeSavedDraft(attrId);
        clearPending(attrId);
        syncDraftDisplay();
      });
    },
    [postDraft, syncDraftDisplay]
  );

  const saveDraftValues = useCallback(
    (attributeId, value) => {
      if (attributeId == null) return Promise.resolve();
      const attrId = String(attributeId);
      const { originals, attributes, users } = getCtx();
      const filtered = filterDraftValuesForSave(
        { [attrId]: value },
        attributes
      );

      if (!Object.hasOwn(filtered, attrId)) {
        clearPending(attrId);
        syncDraftDisplay();
        return Promise.resolve();
      }

      const { trueDraftData } = getTrueDraftDiffs(
        [{ id: attrId, value: filtered[attrId] }],
        originals,
        attributes,
        users
      );

      if (!Object.hasOwn(trueDraftData, attrId)) {
        clearPending(attrId);
        const hadSavedDraft = Object.hasOwn(lastSavedDraftRef.current, attrId);
        removeSavedDraft(attrId);
        syncDraftDisplay();
        return hadSavedDraft
          ? clearDraftAttribute(attributeId, value)
          : Promise.resolve();
      }

      const valueToSave = trueDraftData[attrId];
      if (isEqual(valueToSave, lastSavedDraftRef.current[attrId])) {
        clearPending(attrId);
        syncDraftDisplay();
        return Promise.resolve();
      }

      return postDraft(
        buildDraftApiPayload(attrId, valueToSave),
        (response) => {
          lastSavedDraftRef.current = {
            ...lastSavedDraftRef.current,
            [attrId]: valueToSave,
          };
          clearPending(attrId);
          syncDraftDisplay();
          applyEditorMetaFromResponse(response, [attrId], {
            forceTimestamps: true,
          });
        },
        () => {
          clearPending(attrId);
          syncDraftDisplay();
        }
      );
    },
    [
      postDraft,
      syncDraftDisplay,
      clearDraftAttribute,
      applyEditorMetaFromResponse,
    ]
  );

  const handleAutoSave = useCallback(
    (attributeId, value, options = {}) => {
      const { persist = true, revert = false, debounce = true } = options;
      if (!isMultipleEditMode || !isDraftHydrationCompleteRef.current) return;

      // Modal-based attributes must not auto-draft on every change; only on modal Save
      if (!persist && !revert) {
        const attr = findAttribute(
          allAttributesRef.current,
          String(attributeId)
        );
        if (
          MODAL_DRAFT_DATA_TYPES.includes(attr?.data_type) ||
          MODAL_DRAFT_INPUT_TYPES.includes(attr?.input_control_type)
        ) {
          return;
        }
      }

      const id = String(attributeId);
      const formik = formikFormRef.current;

      const resolvedValue =
        value !== undefined
          ? value
          : (pendingDraftByAttributeRef.current[id] ??
            getFormikValueByAttributeId(formik, attributeId));

      if (revert && persist) {
        clearPending(id);
        queueMicrotask(() => {
          removeSavedDraft(id);
          syncDraftDisplay();
          clearDraftAttribute(id, resolvedValue);
        });
        return;
      }

      if (!persist) {
        pendingDraftByAttributeRef.current[id] = resolvedValue;
        // Auto-save after the user stops typing; when debounce:false, cancel any upstream timer too
        if (debounce !== false) {
          clearTimeout(draftDebounceTimersRef.current[id]);
          draftDebounceTimersRef.current[id] = setTimeout(() => {
            delete draftDebounceTimersRef.current[id];
            queueMicrotask(() => {
              if (draftSaveBlockedRef.current) return;
              const pending = pendingDraftByAttributeRef.current;
              const latestValue = Object.hasOwn(pending, id)
                ? pending[id]
                : (getFormikValueByAttributeId(
                    formikFormRef.current,
                    attributeId
                  ) ?? resolvedValue);
              delete pending[id];
              draftDisplayPendingRef.current[id] = latestValue;
              syncDraftDisplay({ [id]: latestValue });
              saveDraftValues(id, latestValue);
            });
          }, DRAFT_DEBOUNCE_MS);
        } else {
          // Cancel any debounce that an upstream onChange handler may have just started
          clearTimeout(draftDebounceTimersRef.current[id]);
          delete draftDebounceTimersRef.current[id];
        }
        return;
      }

      // Cancel any pending debounce when blur triggers an explicit save
      clearTimeout(draftDebounceTimersRef.current[id]);
      delete draftDebounceTimersRef.current[id];

      queueMicrotask(() => {
        if (draftSaveBlockedRef.current) return;
        const pending = pendingDraftByAttributeRef.current;
        let latestValue = resolvedValue;
        if (Object.hasOwn(pending, id)) {
          latestValue = pending[id];
        } else if (value !== undefined) {
          latestValue = value;
        } else {
          latestValue =
            getFormikValueByAttributeId(formik, attributeId) ?? resolvedValue;
        }
        delete pending[id];
        draftDisplayPendingRef.current[id] = latestValue;
        syncDraftDisplay({ [id]: latestValue });
        saveDraftValues(id, latestValue);
      });
    },
    [isMultipleEditMode, saveDraftValues, syncDraftDisplay, clearDraftAttribute]
  );

  const resetLocalDraftState = useCallback(() => {
    pendingDraftByAttributeRef.current = {};
    draftDisplayPendingRef.current = {};
    Object.values(draftDebounceTimersRef.current).forEach(clearTimeout);
    draftDebounceTimersRef.current = {};
    applyDraftState({}, {}, EMPTY_OBJECT);
    lastSavedDraftRef.current = {};
    return Promise.resolve(handleGetAttributesData?.());
  }, [applyDraftState, handleGetAttributesData]);

  const clearDraftValues = useCallback(async () => {
    if (!entityId || !setDraftAttributeValue) return { success: false };
    draftSaveBlockedRef.current = true;
    pendingDraftByAttributeRef.current = {};
    draftDisplayPendingRef.current = {};
    Object.values(draftDebounceTimersRef.current).forEach(clearTimeout);
    draftDebounceTimersRef.current = {};

    try {
      // Wait for any save that was already in flight to land first, so
      // this clear is guaranteed to be the last write and can't be
      // silently overwritten by a stale save completing afterward.
      await inFlightSaveRequestRef.current;
      await setDraftAttributeValue({
        entity_id: entityId,
        draft: {},
        clear_draft: true,
      });
      await resetLocalDraftState();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      draftSaveBlockedRef.current = false;
    }
  }, [entityId, setDraftAttributeValue, resetLocalDraftState]);

  const collectActiveDrafts = (formik) => {
    const merged = {
      ...lastSavedDraftRef.current,
      ...pendingDraftByAttributeRef.current,
      ...draftDisplayPendingRef.current,
    };
    allAttributesRef.current.forEach((attr) => {
      if (!attributeShowsDraftInput(attr) || attr?.id == null) return;
      const key = String(attr.id);
      merged[key] = getFormikValueByAttributeId(formik, attr.id) ?? merged[key];
    });
    return merged;
  };

  // Lets callers outside this hook (e.g. the background document upload
  // flow, which posts to the draft API itself once a document finishes
  // processing) tell this instance's draft state about a save that already
  // happened, so draftData/draftAttributeIds update and the draft chip shows
  // up without re-POSTing the draft.
  const applyExternalDraftUpdate = useCallback(
    (attributeId, value, response) => {
      // A "discard draft" is in flight — don't let a late-arriving background
      // save resurrect draft state right after the user cleared it.
      if (draftSaveBlockedRef.current) return;
      if (attributeId == null) return;
      const attrId = String(attributeId);
      lastSavedDraftRef.current = {
        ...lastSavedDraftRef.current,
        [attrId]: value,
      };
      clearPending(attrId);
      syncDraftDisplay();
      applyEditorMetaFromResponse(response, [attrId], {
        forceTimestamps: true,
      });
    },
    [clearPending, syncDraftDisplay, applyEditorMetaFromResponse]
  );

  const buildDraftAttributesForPublish = useCallback(() => {
    const drafts = collectActiveDrafts(formikFormRef.current);
    const { originals, attributes, users } = getCtx();
    const values = {};
    attributes.forEach((attr) => {
      if (!attributeShowsDraftInput(attr) || attr?.id == null) return;
      const key = String(attr.id);
      const value = Object.hasOwn(drafts, key) ? drafts[key] : null;
      // Only send attributes whose value actually changed from what's saved.
      if (draftValuesEqual(value, originalValue(originals, key), attr, users)) {
        return;
      }
      values[attr.id] = value;
    });
    return values;
  }, []);

  return {
    draftData,
    draftAttributeIds,
    draftEditorsByAttributeId,
    draftUpdatedAtByAttributeId,
    draftTimestamp,
    hasDraftChanges,
    isLoadingDraft,
    isSavingDraft,
    handleAutoSave,
    buildDraftAttributesForPublish,
    clearDraftValues,
    resetLocalDraftState,
    fetchDraftValues,
    applyDraftValuesToFormik,
    applyExternalDraftUpdate,
  };
};

export default useDraftAutoSave;
